import { useEffect, useState } from 'react';
import { IconQrcode } from '@tabler/icons-react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { notifications } from '@/lib/notifications';
import { copyTextToClipboard } from '../../toolPageUtils';

export default function SignatureMobileScanQrPanel({
    sessionSlug,
    inModal = false,
    onNewSession,
    sessionReady = true,
}) {
    const { t } = useTranslation();

    const [fullUrl, setFullUrl] = useState('');
    const [qrDataUrl, setQrDataUrl] = useState('');
    const [qrError, setQrError] = useState(null);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        if (!sessionSlug) {
            setFullUrl('');
            setQrDataUrl('');
            setQrError(null);
            return undefined;
        }

        const urlStr = `${window.location.origin}/signature-mobile/${sessionSlug}`;
        setFullUrl(urlStr);
        setQrError(null);
        setQrDataUrl('');

        let cancelled = false;
        import('qrcode')
            .then((QRCode) => QRCode.default.toDataURL(urlStr, {
                width: 240,
                margin: 2,
                errorCorrectionLevel: 'M',
                color: { dark: '#1a1a1a', light: '#ffffff' },
            }))
            .then((dataUrl) => {
                if (!cancelled) setQrDataUrl(dataUrl);
            })
            .catch((e) => {
                if (!cancelled) {
                    setQrError(e?.message || t('signaturePadImage.qr.errors.generate'));
                }
            });

        return () => {
            cancelled = true;
        };
    }, [sessionSlug, t]);

    const copyLink = async () => {
        if (!fullUrl) return;
        const ok = copyTextToClipboard(fullUrl);
        if (ok) {
            notifications.show({
                title: t('common.success'),
                message: t('signaturePadImage.qr.linkCopied'),
                color: 'green',
            });
            return;
        }
        try {
            await navigator.clipboard?.writeText(fullUrl);
            notifications.show({
                title: t('common.success'),
                message: t('signaturePadImage.qr.linkCopied'),
                color: 'green',
            });
        } catch {
            notifications.show({
                title: t('common.error'),
                message: t('signaturePadImage.qr.copyFailed'),
                color: 'yellow',
            });
        }
    };

    return (
        <div className="space-y-3">
            {inModal ? (
                <div className="space-y-1 text-[12.5px] leading-relaxed text-[#6B635C] dark:text-zinc-400">
                    <p>{t('signaturePadImage.qr.modalHint')}</p>
                    <p className="text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                        {t('signaturePadImage.qr.oneTimeHint')}
                    </p>
                </div>
            ) : (
                <div className="flex items-start gap-2.5">
                    <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400">
                        <IconQrcode size={18} stroke={1.8} />
                    </div>
                    <div>
                        <p className="text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">
                            {t('signaturePadImage.qr.title')}
                        </p>
                        <p className="mt-0.5 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                            {t('signaturePadImage.qr.oneTimeHint')}
                        </p>
                    </div>
                </div>
            )}

            <div className="flex flex-col items-center gap-2">
                {!sessionSlug ? (
                    <div className="flex h-[240px] w-[240px] items-center justify-center rounded-xl border border-[#ECE8E4] bg-[#F7F5F3] dark:border-zinc-700 dark:bg-zinc-800/50">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
                    </div>
                ) : qrError ? (
                    <p className="text-center text-[12.5px] text-red-500">{qrError}</p>
                ) : qrDataUrl ? (
                    <img
                        src={qrDataUrl}
                        alt=""
                        width={240}
                        height={240}
                        className="block rounded-xl border border-[#ECE8E4] dark:border-zinc-700"
                    />
                ) : (
                    <div className="flex h-[240px] w-[240px] items-center justify-center rounded-xl border border-[#ECE8E4] bg-[#F7F5F3] dark:border-zinc-700 dark:bg-zinc-800/50">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-center gap-2">
                    <button
                        type="button"
                        onClick={copyLink}
                        disabled={!fullUrl}
                        className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-[12px] font-semibold text-violet-700 transition-colors hover:border-violet-300 disabled:opacity-50 dark:border-violet-800 dark:bg-violet-900/30 dark:text-violet-300"
                    >
                        {t('signaturePadImage.qr.copyLink')}
                    </button>
                    {onNewSession ? (
                        <button
                            type="button"
                            onClick={onNewSession}
                            disabled={!sessionReady}
                            className="rounded-lg border border-[#ECE8E4] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#6B635C] transition-colors hover:border-violet-300 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
                        >
                            {t('signaturePadImage.qr.newQr')}
                        </button>
                    ) : null}
                </div>

                {!sessionReady ? (
                    <p className="text-center text-[11.5px] font-semibold text-amber-600 dark:text-amber-400">
                        {t('signaturePadImage.qr.usedHint')}
                    </p>
                ) : null}
            </div>
        </div>
    );
}
